import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { branchTablesAPI, getApiErrorMessage, heldOrdersAPI, salesmenAPI } from '@matix/api-client';
import { POS_PERMISSIONS } from '@matix/types';
import type { BranchFloorDto, BranchTableDto } from '@matix/api-client';
import { useUI } from '../../store/ui';
import { useCart, type CartTable } from '../../store/cart';
import { toast } from '../../store/toast';
import { XIcon } from '../../components/icons';
import { useLang } from '../../lib/language';
import { useAuth } from '@matix/auth';
import { openHeldOrder } from '../held-orders/openHeld';
import { useCurrency } from '../print/useCurrency';
import { formatTimeAgo } from '../parked-orders/helpers';
import { useCanI } from '../../lib/permissions';
import { reloadAfterTableAction, requireSavedHeld, tableActionErrorText } from '../held-orders/tableActions';
import { nameFor } from '../../lib/localizedName';

type HeldOnTable = NonNullable<BranchTableDto['held_orders']>[number];

/**
 * Table colours (the legacy till's palette, new-style.css .modal_NewDineIn):
 *   free      — no held order on it
 *   occupied  — a held order sits on it
 *   openOther — that order is OPEN in another cashier's till right now
 *   thisOrder — the table of the order in this cart
 */
const TABLE_COLORS = {
  free: '#FFFFFF',
  freeText: '#0C5EFF',
  occupied: '#F98A8A',
  openOther: '#BB3838',
  thisOrder: '#6A84BE',
} as const;

type TileState = 'free' | 'occupied' | 'openOther' | 'thisOrder';

/**
 * The legacy "Choose Table" screen's action buttons, for a HELD order open in
 * the cart (each behind its own legacy permission):
 *   change — the next FREE table tapped becomes the order's table;
 *   move   — the next table tapped, free or not; an order already on it is
 *            merged into this one;
 *   merge  — tap tables to join them, then Merge: their held orders are
 *            absorbed and this order occupies them all.
 *   waiter — tap an occupied table and pick its waiter: the order is credited
 *            to them (Empoyee_ID, the name on the tile). Same list and
 *            permission as "Change Salesman"; needs no order in the cart.
 */
type TableAction = 'none' | 'change' | 'move' | 'merge' | 'waiter';

/**
 * Table picker modal — attaches a BranchTables row to the in-progress cart.
 *
 * Layout: floor pill row on top (All / Floor 1 / Floor 2 / …), then a grid
 * of tables under the active floor. Click a table → set in cart store,
 * close. The same table id flows into the create-order request as
 * `branch_table_id` so `Inv_Receipt_h.BranchTableId` is set on save and
 * the receipt + kitchen ticket print the table label.
 *
 * A table is TAKEN while a held order sits on it — the legacy rule
 * (GetAllBranchTables: IsHolded = 1, not cancelled) — and frees itself when
 * that order is paid or cancelled. Drawn the way the legacy "Choose Table"
 * screen drew it, colour for colour:
 *
 *   white, blue number   free                          → attaches to this order
 *   pink   #F98A8A       occupied: a held order on it  → opens that order
 *   red    #BB3838       that order is OPEN in another  → opens it too, after
 *                        cashier's till right now          saying who has it
 *   blue   #6A84BE       the table of the order in this cart
 *
 * A taken table shows the order number, who holds it, the amount and how
 * long ago it was opened (the legacy tile showed only the waiter; the rest is
 * what a floor manager asks for). A table carrying several held orders offers
 * a chooser, as the legacy till did. Whoever picks a taken table gets the
 * EXISTING order back to continue it; it is never attached to a new order —
 * the server refuses that too (TABLE_OCCUPIED). "Open right now" is the
 * heartbeat a till sends while a held order sits in its cart.
 */
export function TablePickerModal() {
  const isOpen = useUI((s) => s.openModal === 'tablePicker');
  const closeModal = useUI((s) => s.closeModal);
  const setTable = useCart((s) => s.setTable);
  const currentTable = useCart((s) => s.table);
  const { t: tr, lang } = useLang();
  const { cashier } = useAuth();
  const me = cashier?.sub ?? null;
  const editingReceiptId = useCart((s) => s.editingReceiptId);
  const currency = useCurrency();
  const money = (n: number): string =>
    `${(Number.isFinite(n) ? n : 0).toFixed(Math.max(0, Math.min(8, currency.precision)))} ${currency.code}`;

  const [activeFloorId, setActiveFloorId] = useState<number | 'all'>('all');
  /** A taken table carrying SEVERAL held orders — which one to open. */
  const [chooser, setChooser] = useState<{ table: BranchTableDto; orders: HeldOnTable[] } | null>(null);
  const editingHeld = useCart((s) => s.editingHeld);
  const canChange = useCanI(POS_PERMISSIONS.ChangeTables);
  const canMove = useCanI(POS_PERMISSIONS.MoveTables);
  const canMerge = useCanI(POS_PERMISSIONS.JoinTables);
  /** The actions apply to a held order that exists on the server. */
  const actionsAvailable = editingHeld && editingReceiptId != null && (canChange || canMove || canMerge);
  const canWaiter = useCanI(POS_PERMISSIONS.ChangeSalesman);
  /** The occupied table whose waiter is being picked, and which of its orders. */
  const [waiterFor, setWaiterFor] = useState<{ table: BranchTableDto; orders: HeldOnTable[]; orderId: number } | null>(null);
  const waitersQuery = useQuery({
    queryKey: ['receiptCreators'],
    queryFn: () => salesmenAPI.creators(),
    enabled: isOpen && canWaiter,
    staleTime: 5 * 60 * 1000,
  });
  const [action, setAction] = useState<TableAction>('none');
  const [mergeSel, setMergeSel] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();

  const floorsQuery = useQuery<BranchFloorDto[]>({
    queryKey: ['branch', 'floors'],
    queryFn: () => branchTablesAPI.listFloors(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // floors change rarely
  });

  const tablesQuery = useQuery<BranchTableDto[]>({
    queryKey: ['branch', 'tables', activeFloorId],
    queryFn: () =>
      branchTablesAPI.listTables(activeFloorId === 'all' ? null : activeFloorId),
    enabled: isOpen,
    // Always fresh on open: a table taken or freed a moment ago on another
    // till must show as such, or the cashier seats a second party on it.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (!isOpen) {
      setActiveFloorId('all');
      setChooser(null);
      setAction('none');
      setMergeSel([]);
      setWaiterFor(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (floorsQuery.error) toast(getApiErrorMessage(floorsQuery.error), 'error');
  }, [floorsQuery.error]);
  useEffect(() => {
    if (tablesQuery.error) toast(getApiErrorMessage(tablesQuery.error), 'error');
  }, [tablesQuery.error]);

  const floorNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const f of floorsQuery.data ?? []) m.set(f.id, f.name);
    return m;
  }, [floorsQuery.data]);

  if (!isOpen) return null;

  function tableLabel(t: BranchTableDto): string {
    if (t.name && t.name.trim().length > 0) return t.name.trim();
    return tr({ en: `Table ${t.table_number}`, ar: `طاولة ${t.table_number}` });
  }

  /** The held orders on a table OTHER than the one being edited now. */
  function othersOn(t: BranchTableDto): HeldOnTable[] {
    return (t.held_orders ?? []).filter((o) => o.receipt_id !== editingReceiptId);
  }

  /** The first held order on the table that ANOTHER cashier has open now. */
  function openByOther(t: BranchTableDto): HeldOnTable | undefined {
    return othersOn(t).find((o) => o.open_by != null && o.open_by.user_id !== me);
  }

  function tableState(t: BranchTableDto): TileState {
    if (editingReceiptId != null && (t.held_orders ?? []).some((o) => o.receipt_id === editingReceiptId)) {
      return 'thisOrder';
    }
    const others = othersOn(t);
    if (others.length === 0) return currentTable?.id === t.id ? 'thisOrder' : 'free';
    return openByOther(t) ? 'openOther' : 'occupied';
  }

  async function openOrder(o: HeldOnTable) {
    // Another cashier has it open right now: say who, and let them decide.
    if (o.open_by && o.open_by.user_id !== me) {
      const who = o.open_by.user_name ?? tr({ en: 'another cashier', ar: 'كاشير آخر' });
      const ok = window.confirm(
        tr({
          en: `${who} has order #${o.entry_no} open right now. Open it anyway?`,
          ar: `الطلب #${o.entry_no} مفتوح الآن لدى ${who}. هل تريد فتحه على أي حال؟`,
        }),
      );
      if (!ok) return;
    }
    const opened = await openHeldOrder(o.receipt_id, { pay: false, tr });
    if (opened) {
      setChooser(null);
      closeModal();
    }
  }

  /** Run a table action, then reload the order and the tables. */
  async function runAction(fn: () => Promise<Parameters<typeof reloadAfterTableAction>[0]>, done: string) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fn();
      await queryClient.invalidateQueries({ queryKey: ['branch', 'tables'] });
      await reloadAfterTableAction(res);
      const merged = res.absorbed.map((a) => `#${a.entry_no}`).join(', ');
      toast(
        merged ? `${done} · ${tr({ en: `merged ${merged}`, ar: `تم دمج ${merged}` })}` : done,
        'success',
      );
      closeModal();
    } catch (err) {
      toast(tableActionErrorText(err) ?? getApiErrorMessage(err), 'error');
      void queryClient.invalidateQueries({ queryKey: ['branch', 'tables'] });
    } finally {
      setBusy(false);
    }
  }

  function startAction(next: TableAction) {
    // Assigning a waiter changes no line, so unsaved cart edits do not matter.
    if (next !== 'none' && next !== 'waiter' && !requireSavedHeld()) return;
    setChooser(null);
    setWaiterFor(null);
    setMergeSel([]);
    setAction((cur) => (cur === next ? 'none' : next));
  }

  async function assignWaiter(orderId: number, user: { id: number; name: string; name_ar: string | null }) {
    if (busy) return;
    setBusy(true);
    try {
      await heldOrdersAPI.assignWaiter(orderId, user.id);
      // The order open in this cart shows (and re-holds) its waiter too.
      if (orderId === editingReceiptId) {
        useCart.getState().setCreator({ id: user.id, name: user.name, nameAr: user.name_ar });
      }
      await queryClient.invalidateQueries({ queryKey: ['branch', 'tables'] });
      toast(
        tr({
          en: `Waiter: ${nameFor(lang, user, '')}`,
          ar: `النادل: ${nameFor(lang, user, '')}`,
        }),
        'success',
      );
      setWaiterFor(null);
    } catch (err) {
      toast(tableActionErrorText(err) ?? getApiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  function handleActionClick(t: BranchTableDto) {
    if (action === 'waiter') {
      const orders = t.held_orders ?? [];
      if (orders.length === 0) {
        toast(
          tr({ en: 'This table has no order yet — a waiter goes on an order.', ar: 'لا يوجد طلب على هذه الطاولة بعد — النادل يُعيَّن على الطلب.' }),
          'warning',
        );
        return;
      }
      setWaiterFor({ table: t, orders, orderId: orders[0]!.receipt_id });
      return;
    }
    const id = editingReceiptId!;
    const mine = tableState(t) === 'thisOrder';
    const others = othersOn(t);
    if (action === 'merge') {
      if (mine) return;
      setMergeSel((sel) => (sel.includes(t.id) ? sel.filter((x) => x !== t.id) : [...sel, t.id]));
      return;
    }
    if (mine) {
      toast(tr({ en: 'The order is already on this table.', ar: 'الطلب على هذه الطاولة بالفعل.' }), 'warning');
      return;
    }
    if (action === 'change') {
      if (others.length > 0) {
        toast(
          tr({
            en: 'Change needs a free table. Use Move to join an occupied one.',
            ar: 'التغيير يحتاج طاولة متاحة. استخدم نقل للانضمام لطاولة مشغولة.',
          }),
          'warning',
        );
        return;
      }
      void runAction(
        () => heldOrdersAPI.moveTable(id, { branch_table_id: t.id, mode: 'change' }),
        tr({ en: `Moved to ${tableLabel(t)}`, ar: `تم النقل إلى ${tableLabel(t)}` }),
      );
      return;
    }
    // move
    if (others.length > 0) {
      const nums = others.map((o) => `#${o.entry_no}`).join(', ');
      const ok = window.confirm(
        tr({
          en: `${tableLabel(t)} has order ${nums}. Move this order there and merge ${nums} into it?`,
          ar: `على ${tableLabel(t)} الطلب ${nums}. هل تنقل هذا الطلب إليها وتدمج ${nums} فيه؟`,
        }),
      );
      if (!ok) return;
    }
    void runAction(
      () => heldOrdersAPI.moveTable(id, { branch_table_id: t.id, mode: 'move' }),
      tr({ en: `Moved to ${tableLabel(t)}`, ar: `تم النقل إلى ${tableLabel(t)}` }),
    );
  }

  function confirmMerge() {
    if (mergeSel.length === 0 || editingReceiptId == null) return;
    const chosen = tables.filter((t) => mergeSel.includes(t.id));
    const absorbing = chosen.flatMap((t) => othersOn(t).map((o) => `#${o.entry_no}`));
    const ok = window.confirm(
      tr({
        en: `Join ${chosen.map(tableLabel).join(', ')} to this order${absorbing.length ? `, merging ${absorbing.join(', ')} into it` : ''}?`,
        ar: `ضم ${chosen.map(tableLabel).join('، ')} إلى هذا الطلب${absorbing.length ? ` ودمج ${absorbing.join('، ')} فيه` : ''}؟`,
      }),
    );
    if (!ok) return;
    void runAction(
      () => heldOrdersAPI.mergeTables(editingReceiptId, mergeSel),
      tr({ en: 'Tables merged', ar: 'تم دمج الطاولات' }),
    );
  }

  function handleClick(t: BranchTableDto) {
    if (action !== 'none') {
      handleActionClick(t);
      return;
    }
    const others = othersOn(t);
    if (others.length === 0) {
      handlePick(t);
      return;
    }
    // Taken: whoever picks it gets the EXISTING order back to carry on with —
    // a table never gets a second order (the server refuses that too).
    if (others.length === 1) {
      void openOrder(others[0]!);
      return;
    }
    setChooser({ table: t, orders: others });
  }

  function handlePick(t: BranchTableDto) {
    const picked: CartTable = {
      id: t.id,
      label: tableLabel(t),
      tableNumber: t.table_number,
    };
    setTable(picked);
    toast(tr({ en: `Attached to ${picked.label}`, ar: `تم الربط بـ ${picked.label}` }), 'success');
    closeModal();
  }

  function handleClear() {
    setTable(null);
    closeModal();
  }

  const floors = floorsQuery.data ?? [];
  const tables = [...(tablesQuery.data ?? [])].sort(
    (a, b) => (a.table_number ?? 0) - (b.table_number ?? 0),
  );

  return (
    <div className="modal-overlay show" onClick={closeModal}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
      >
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--primary-soft, #e0e7ff)', color: 'var(--primary, #4f46e5)' }}
          >
            T
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{tr({ en: 'Pick a table', ar: 'اختر طاولة' })}</div>
            <div className="modal-header-name-ar">
              {currentTable ? tr({ en: `Currently: ${currentTable.label}`, ar: `حاليًا: ${currentTable.label}` }) : tr({ en: 'No table attached', ar: 'لا توجد طاولة مرتبطة' })}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={closeModal}
            aria-label={tr({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ padding: 16 }}>
          {(actionsAvailable || canWaiter) && (
            <div style={actionBarStyle}>
              {actionsAvailable && canChange && (
                <ActionPill
                  active={action === 'change'}
                  color="#D97706"
                  label={tr({ en: 'Change', ar: 'تغيير' })}
                  onClick={() => startAction('change')}
                  disabled={busy}
                />
              )}
              {actionsAvailable && canMove && (
                <ActionPill
                  active={action === 'move'}
                  color="#DC2626"
                  label={tr({ en: 'Move', ar: 'نقل' })}
                  onClick={() => startAction('move')}
                  disabled={busy}
                />
              )}
              {actionsAvailable && canMerge && (
                <ActionPill
                  active={action === 'merge'}
                  color="#16A34A"
                  label={tr({ en: 'Merge', ar: 'دمج' })}
                  onClick={() => startAction('merge')}
                  disabled={busy}
                />
              )}
              {canWaiter && (
                <ActionPill
                  active={action === 'waiter'}
                  color="#7C3AED"
                  label={tr({ en: 'Waiter', ar: 'النادل' })}
                  onClick={() => startAction('waiter')}
                  disabled={busy}
                />
              )}
              <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: '1 1 200px' }}>
                {action === 'change'
                  ? tr({ en: 'Tap a free table to move this order to it.', ar: 'اضغط طاولة متاحة لنقل الطلب إليها.' })
                  : action === 'move'
                    ? tr({
                        en: 'Tap any table. An order already on it is merged into this one.',
                        ar: 'اضغط أي طاولة. الطلب الموجود عليها يُدمج في هذا الطلب.',
                      })
                    : action === 'merge'
                      ? tr({
                          en: 'Tap the tables to join, then press Merge below.',
                          ar: 'اضغط الطاولات المراد ضمها، ثم اضغط دمج بالأسفل.',
                        })
                      : action === 'waiter'
                        ? tr({ en: 'Tap an occupied table to assign its waiter.', ar: 'اضغط طاولة مشغولة لتعيين النادل لها.' })
                        : actionsAvailable
                          ? tr({ en: 'Table actions for this held order', ar: 'إجراءات الطاولة لهذا الطلب المعلق' })
                          : ''}
              </span>
            </div>
          )}

          {/* Floor filter pills */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <FloorPill
              label={tr({ en: 'All', ar: 'الكل' })}
              active={activeFloorId === 'all'}
              onClick={() => setActiveFloorId('all')}
            />
            {floors.map((f) => (
              <FloorPill
                key={f.id}
                label={`${f.name} (${f.tables_count})`}
                active={activeFloorId === f.id}
                onClick={() => setActiveFloorId(f.id)}
              />
            ))}
          </div>

          {tablesQuery.isLoading ? (
            <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
              {tr({ en: 'Loading tables…', ar: 'جارٍ تحميل الطاولات…' })}
            </div>
          ) : tables.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
              {activeFloorId === 'all'
                ? tr({ en: 'No tables configured for this branch yet.', ar: 'لا توجد طاولات مهيأة لهذا الفرع بعد.' })
                : tr({ en: 'No tables on this floor.', ar: 'لا توجد طاولات في هذا الطابق.' })}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: 10,
              }}
            >
              {tables.map((t) => {
                const state = tableState(t);
                const others = othersOn(t);
                const first = others[0];
                const onDark = state !== 'free';
                const sub = { fontSize: 11, color: onDark ? 'rgba(255,255,255,0.92)' : 'var(--text-muted)' };
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleClick(t)}
                    disabled={busy}
                    style={{
                      ...tableTile(state),
                      ...(action === 'merge' && mergeSel.includes(t.id)
                        ? { outline: '4px solid #16A34A', outlineOffset: 2 }
                        : {}),
                      ...(action === 'change' && state !== 'free' && state !== 'thisOrder' ? { opacity: 0.45 } : {}),
                    }}
                    title={
                      first
                        ? tr({
                            en: `Order #${first.entry_no} · ${first.employee_name ?? ''}`,
                            ar: `طلب #${first.entry_no} · ${first.employee_name ?? ''}`,
                          })
                        : undefined
                    }
                  >
                    <span style={{ fontSize: 18, fontWeight: 800 }}>{tableLabel(t)}</span>
                    {t.floor_id && activeFloorId === 'all' && (
                      <span style={sub}>{floorNameById.get(t.floor_id) ?? ''}</span>
                    )}
                    {first ? (
                      <>
                        <span style={{ ...sub, fontWeight: 700, fontSize: 12 }}>
                          #{first.entry_no}
                          {others.length > 1 ? ` +${others.length - 1}` : ''}
                        </span>
                        {first.employee_name && (
                          <span style={{ ...sub, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {first.employee_name}
                          </span>
                        )}
                        {state === 'openOther' && (
                          <span style={{ ...sub, fontWeight: 700, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tr({
                              en: `Open by ${openByOther(t)?.open_by?.user_name ?? '—'}`,
                              ar: `مفتوح لدى ${openByOther(t)?.open_by?.user_name ?? '—'}`,
                            })}
                          </span>
                        )}
                        <span style={sub}>
                          {money(first.net_value)} · {formatTimeAgo(first.entry_date)}
                        </span>
                      </>
                    ) : (
                      <span style={sub}>
                        {state === 'thisOrder'
                          ? tr({ en: 'This order', ar: 'هذا الطلب' })
                          : t.seats_count
                            ? tr({ en: `${t.seats_count} seats`, ar: `${t.seats_count} مقعد` })
                            : tr({ en: 'Available', ar: 'متاحة' })}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Its own box, on a grey that is not white, so the white Available
            swatch is visible against it. Fixed colours rather than theme
            tokens: the swatches are fixed, so their backdrop must be too. */}
        <div style={legendBoxStyle} role="note" aria-label={tr({ en: 'Table colours', ar: 'ألوان الطاولات' })}>
          <span style={{ fontWeight: 700, fontSize: 12 }}>{tr({ en: 'Table colours:', ar: 'ألوان الطاولات:' })}</span>
          <LegendChip color={TABLE_COLORS.free} border label={tr({ en: 'Available', ar: 'متاحة' })} />
          <LegendChip color={TABLE_COLORS.occupied} label={tr({ en: 'Occupied (has an order)', ar: 'مشغولة (عليها طلب)' })} />
          <LegendChip color={TABLE_COLORS.openOther} label={tr({ en: 'Open by another user', ar: 'مفتوحة لدى مستخدم آخر' })} />
          <LegendChip color={TABLE_COLORS.thisOrder} label={tr({ en: 'This order', ar: 'هذا الطلب' })} />
        </div>

        {waiterFor && (
          <div style={chooserStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              {tr({ en: `Waiter for ${tableLabel(waiterFor.table)}`, ar: `النادل لـ ${tableLabel(waiterFor.table)}` })}
            </div>
            {waiterFor.orders.length > 1 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {waiterFor.orders.map((o) => (
                  <button
                    key={o.receipt_id}
                    type="button"
                    className={o.receipt_id === waiterFor.orderId ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setWaiterFor({ ...waiterFor, orderId: o.receipt_id })}
                  >
                    #{o.entry_no}
                  </button>
                ))}
              </div>
            )}
            {waitersQuery.isLoading ? (
              <div style={{ color: 'var(--text-muted)' }}>{tr({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
                {(waitersQuery.data?.users ?? []).map((u) => {
                  const current = waiterFor.orders.find((o) => o.receipt_id === waiterFor.orderId)?.employee_id === u.id;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={current ? 'btn-primary' : 'btn-secondary'}
                      disabled={busy}
                      onClick={() => void assignWaiter(waiterFor.orderId, u)}
                    >
                      {nameFor(lang, u, `#${u.id}`)}
                    </button>
                  );
                })}
                <button type="button" className="btn-secondary" onClick={() => setWaiterFor(null)}>
                  {tr({ en: 'Back', ar: 'رجوع' })}
                </button>
              </div>
            )}
          </div>
        )}

        {chooser && (
          <div style={chooserStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              {tr({
                en: `${tableLabel(chooser.table)} has ${chooser.orders.length} held orders — open which?`,
                ar: `${tableLabel(chooser.table)} عليها ${chooser.orders.length} طلبات معلقة — أيها تفتح؟`,
              })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {chooser.orders.map((o) => (
                <button key={o.receipt_id} type="button" className="btn-secondary" onClick={() => void openOrder(o)}>
                  #{o.entry_no} · {o.employee_name ?? ''} · {money(o.net_value)}
                </button>
              ))}
              <button type="button" className="btn-secondary" onClick={() => setChooser(null)}>
                {tr({ en: 'Back', ar: 'رجوع' })}
              </button>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <div />
          <div className="modal-actions">
            {action === 'merge' && (
              <button
                type="button"
                className="btn-primary"
                disabled={busy || mergeSel.length === 0}
                onClick={confirmMerge}
              >
                {tr({ en: `Merge (${mergeSel.length})`, ar: `دمج (${mergeSel.length})` })}
              </button>
            )}
            {currentTable && action === 'none' && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClear}
              >
                {tr({ en: 'Detach', ar: 'إلغاء الربط' })}
              </button>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={closeModal}
            >
              {tr({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionPill({
  label, active, color, onClick, disabled,
}: {
  label: string;
  active: boolean;
  color: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      style={{
        padding: '8px 16px',
        borderRadius: 8,
        border: `2px solid ${color}`,
        background: active ? color : 'transparent',
        color: active ? '#fff' : color,
        fontSize: 14,
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
        minHeight: 40,
      }}
    >
      {label}
    </button>
  );
}

const actionBarStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
  marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border, #e5e7eb)',
};

function FloorPill({
  label, active, onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid',
        borderColor: active ? 'var(--primary, #4f46e5)' : 'var(--border, #e5e7eb)',
        background: active ? 'var(--primary, #4f46e5)' : 'transparent',
        color: active ? '#fff' : 'var(--text)',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function tableTile(state: TileState): React.CSSProperties {
  const bg =
    state === 'occupied' ? TABLE_COLORS.occupied
      : state === 'openOther' ? TABLE_COLORS.openOther
        : state === 'thisOrder' ? TABLE_COLORS.thisOrder
          : TABLE_COLORS.free;
  return {
    position: 'relative',
    padding: '12px 8px',
    borderRadius: 10,
    border: '2px solid',
    borderColor: state === 'free' ? 'var(--border, #e5e7eb)' : bg,
    background: bg,
    color: state === 'free' ? TABLE_COLORS.freeText : '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    minHeight: 96,
    cursor: 'pointer',
    boxShadow: state === 'thisOrder' ? '0 0 0 3px rgba(106,132,190,0.35)' : 'none',
  };
}

function LegendChip({ color, label }: { color: string; label: string; border?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
      <span
        style={{
          width: 18, height: 18, borderRadius: 5, background: color,
          border: '1.5px solid #64748B', flex: '0 0 auto',
        }}
      />
      {label}
    </span>
  );
}

const legendBoxStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14,
  margin: '0 16px 12px', padding: '10px 14px',
  borderRadius: 12, border: '1px solid #94A3B8',
  background: '#E2E8F0', color: '#1E293B',
};

const chooserStyle: React.CSSProperties = {
  margin: '0 16px 12px', padding: 12, borderRadius: 10,
  border: '1px solid var(--border, #e5e7eb)', background: 'var(--surface-2, #f8fafc)',
};

